import { useEffect, useRef } from 'react'
import { DEFAULT_SHORTCUTS, type ShortcutBinding } from '@/hooks/useKeyboardShortcuts'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { SHORTCUTS_STORAGE_KEY } from '@/hooks/useKeyboardShortcuts'

// ─── Category metadata ────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<ShortcutBinding['category'], string> = {
  stream: 'Stream Control',
  navigation: 'Navigation',
  chat: 'Chat',
  general: 'General',
}

const CATEGORY_ORDER: ShortcutBinding['category'][] = ['stream', 'navigation', 'chat', 'general']

// ─── Kbd display ──────────────────────────────────────────────────────────────

function KbdChip({ part }: { part: string }) {
  return (
    <kbd
      className="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-mono"
      style={{
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: '#a78bfa',
        minWidth: '1.5rem',
      }}
    >
      {part}
    </kbd>
  )
}

function ComboDisplay({ combo }: { combo: string }) {
  const parts = combo.split('+')
  return (
    <span className="flex items-center gap-1" aria-label={combo}>
      {parts.map((part, i) => (
        <KbdChip key={i} part={part} />
      ))}
    </span>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ShortcutsHelpProps {
  open: boolean
  onClose: () => void
}

export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps) {
  const [shortcuts] = useLocalStorage<ShortcutBinding[]>(SHORTCUTS_STORAGE_KEY, DEFAULT_SHORTCUTS)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Focus trap + Escape
  useEffect(() => {
    if (!open) return

    const prevFocus = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }

    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      prevFocus?.focus()
    }
  }, [open, onClose])

  if (!open) return null

  // Group by category in defined order
  const grouped = CATEGORY_ORDER.reduce<Record<string, ShortcutBinding[]>>((acc, cat) => {
    const items = shortcuts.filter((s) => s.category === cat)
    if (items.length > 0) acc[cat] = items
    return acc
  }, {})

  // Also catch any uncategorised items
  const knownCats = new Set(CATEGORY_ORDER)
  const other = shortcuts.filter((s) => !knownCats.has(s.category))
  if (other.length > 0) grouped['other'] = other

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        tabIndex={-1}
        className="relative w-full rounded-2xl shadow-2xl overflow-hidden outline-none"
        style={{
          maxWidth: '56rem',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--bg-card)',
          border: '1px solid #1e1e3a',
        }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 shrink-0"
          style={{ borderBottom: '1px solid #1e1e3a' }}
        >
          <div>
            <h2 className="text-white text-lg font-semibold">Keyboard Shortcuts</h2>
            <p className="text-gray-500 text-xs mt-0.5">
              Press <KbdChip part="?" /> to toggle this panel &middot;{' '}
              <a href="/settings?tab=shortcuts" className="text-purple-400 hover:text-purple-300 transition-colors">
                Customize shortcuts
              </a>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close shortcuts panel"
            className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-white transition-colors"
            style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
          >
            ✕
          </button>
        </div>

        {/* Grid of categories */}
        <div className="overflow-y-auto p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {Object.entries(grouped).map(([cat, bindings]) => (
              <section key={cat}>
                <h3
                  className="text-xs font-semibold uppercase tracking-wider mb-3"
                  style={{ color: 'var(--text-muted)' }}
                >
                  {CATEGORY_LABELS[cat as ShortcutBinding['category']] ?? cat}
                </h3>
                <ul className="space-y-2">
                  {bindings.map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-4">
                      <span className="text-sm text-gray-300">{s.label}</span>
                      <ComboDisplay combo={s.combo} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 shrink-0 flex items-center gap-4 text-xs"
          style={{ borderTop: '1px solid #1e1e3a', color: '#4b5563' }}
          aria-hidden="true"
        >
          <span className="flex items-center gap-1.5">
            <KbdChip part="ESC" />
            close
          </span>
          <span className="flex items-center gap-1.5">
            <KbdChip part="?" />
            toggle
          </span>
        </div>
      </div>
    </div>
  )
}
